import React, { useMemo, useState } from "react";
import { useQuery } from "urql";
import { Link, useParams } from "wouter";
import { Form, Formik } from "formik";
import stableStringify from "json-stable-stringify";

import { Button } from "../../../components/src/Button";
import { Label, TextField, NumberField, DateField } from "../../../components/src/form";
import { BROKER_TRANSACTIONS_AND_CLIENTS_QUERY } from "../../../graphql/src/brokertransaction/queries";
import { BrokerTransaction } from "../types.generated";

export const BrokerTransactionPage = (): React.ReactElement => {
    const params = useParams<{ id: string }>();
    const id = params.id;
    const [showVersions, setShowVersions] = useState(false);

    const [{ fetching, error, data }] = useQuery<{ brokerTransactions?: Partial<BrokerTransaction>[] }>({
        query: BROKER_TRANSACTIONS_AND_CLIENTS_QUERY,
        requestPolicy: "cache-and-network",
        variables: { filter: { idIn: [id] }, includeVersions: showVersions }
    });

    const { brokerTransaction, versions } = useMemo(() => {
        if (!data?.brokerTransactions?.length) {
            return { brokerTransaction: null, versions: [] };
        }
        const transaction = data.brokerTransactions[0];
        return {
            brokerTransaction: transaction,
            versions: transaction.versions || []
        };
    }, [data?.brokerTransactions]);

    if (fetching) return <div>Loading...</div>;
    if (error)
        return (
            <div>
                <p>error:</p>
                <pre>{JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    if (!brokerTransaction)
        return (
            <div>
                <p>No broker transaction found</p>
            </div>
        );

    const initialValues = {
        ...brokerTransaction,
        clientName: brokerTransaction.client ? brokerTransaction.client.name : null,
        brokerName: brokerTransaction.broker ? brokerTransaction.broker.name : null,
        instrumentName: brokerTransaction.instrument ? brokerTransaction.instrument.name : null,
        updateUserName: brokerTransaction.updateUserInfo ? brokerTransaction.updateUserInfo.name : null
    };

    return (
        <div>
            <div className="mb-2">
                <Link to="/brokertransactions">Broker transactions</Link>
            </div>

            <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={() => {}}>
                {() => (
                    <Form autoComplete="off">
                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField
                                    name="clientName"
                                    label={
                                        brokerTransaction.client ? (
                                            <Label
                                                name="clientName"
                                                value={<Link to={`/parties/${brokerTransaction.client._id}`}>Client</Link>}
                                            />
                                        ) : (
                                            "Client"
                                        )
                                    }
                                    className=""
                                    disabled={true}
                                />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField
                                    name="brokerName"
                                    label={
                                        brokerTransaction.broker ? (
                                            <Label
                                                name="brokerName"
                                                value={<Link to={`/parties/${brokerTransaction.broker._id}`}>Broker</Link>}
                                            />
                                        ) : (
                                            "Broker"
                                        )
                                    }
                                    className=""
                                    disabled={true}
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <DateField name="tradeDate" label="Trade date" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <DateField name="valueDate" label="Value date" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <TextField name="currency" label="Currency" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <TextField
                                    name="instrumentName"
                                    label={
                                        brokerTransaction.instrument ? (
                                            <Label
                                                name="instrumentName"
                                                value={<Link to={`/instruments/${brokerTransaction.instrument._id}`}>Instrument</Link>}
                                            />
                                        ) : (
                                            "Instrument"
                                        )
                                    }
                                    className=""
                                    disabled={true}
                                />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <NumberField name="quantity" label="Quantity" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <NumberField name="price" label="Price" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <NumberField name="commission" label="Commission" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <NumberField name="stampDuty" label="Stamp duty" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <NumberField name="settlementAmount" label="Settlement amount" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="description" label="Description" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="status" label="Status" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="error" label="Error" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="externalId" label="External id" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full px-2 mb-4">
                                <TextField name="correspondingTransactionId" label="Matched with" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full px-2 mb-4">
                                <TextField name="_id" label="Id" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="createTimestamp" label="Create timestamp" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="updateTimestamp" label="Update timestamp" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full px-2 mb-4">
                                <TextField name="updateUserName" label="Update user" className="" disabled={true} />
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

            <Button variant="primary" onClick={() => setShowVersions(!showVersions)}>
                {showVersions ? "Hide versions" : "Show versions"}
            </Button>

            {showVersions ? (
                <div className="m-2">
                    <pre>{stableStringify(versions, { space: 2 })}</pre>
                </div>
            ) : null}
        </div>
    );
};
